import { useState, useEffect } from "react";
import { portfolioVideos } from "@/data/portfolioVideos";
import VideoModal from "@/components/VideoModal";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Skeleton } from "@/components/ui/skeleton";

const VideoPortfolio = () => {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  const topGrid = portfolioVideos.slice(0, 19);
  const bottomGrid = portfolioVideos.slice(19);

  useEffect(() => {
    const scriptSrc = "https://cdn.commoninja.com/sdk/latest/commonninja.js";
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    portfolioVideos.forEach((id) => {
      fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${id}&width=1280`)
        .then((r) => r.json())
        .then((data) => {
          if (data.thumbnail_url) {
            setThumbnails((prev) => ({ ...prev, [id]: data.thumbnail_url }));
          }
        })
        .catch(() => {});
    });
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <Navbar />

      <section className="flex flex-col items-center justify-center text-center pt-8 pb-5 bg-black">
        <h1 className="text-4xl md:text-[4rem] font-bold text-section-dark-foreground mb-4">
          video portfolio
        </h1>
      </section>

      <section className="bg-white px-4 md:px-12 py-0 space-y-5">
        <div className="max-w-6xl mx-auto">
          <div className="commonninja_component pid-ce6d61ea-6bd8-4dbd-9caa-fa63666b9994"></div>
        </div>
        <div className="max-w-6xl mx-auto">
          <div className="commonninja_component pid-319e686a-5af2-4e24-aa12-2f9ea5af11e2"></div>
        </div>
      </section>

      <section id="video-grid" className="px-4 md:px-12 pt-5 bg-white pb-[50px]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            {topGrid.slice(0, 2).map((id) => (
              <Thumbnail key={id} vimeoId={id} thumb={thumbnails[id]} onClick={setActiveVideo} />
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mb-5">
            <div className="md:col-span-7 flex flex-col gap-5">
              {topGrid.slice(2, 4).map((id) => (
                <Thumbnail key={id} vimeoId={id} thumb={thumbnails[id]} onClick={setActiveVideo} />
              ))}
            </div>
            <div className="md:col-span-5 flex flex-col gap-5">
              {topGrid.slice(4, 7).map((id) => (
                <Thumbnail key={id} vimeoId={id} thumb={thumbnails[id]} onClick={setActiveVideo} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-5">
            <div className="flex flex-col gap-5">
              {topGrid.slice(7, 10).map((id) => (
                <Thumbnail key={id} vimeoId={id} thumb={thumbnails[id]} onClick={setActiveVideo} />
              ))}
            </div>
            <div className="flex flex-col gap-5">
              {topGrid.slice(10, 13).map((id) => (
                <Thumbnail key={id} vimeoId={id} thumb={thumbnails[id]} onClick={setActiveVideo} />
              ))}
            </div>
            <div className="flex flex-col gap-5">
              {topGrid.slice(13, 16).map((id) => (
                <Thumbnail key={id} vimeoId={id} thumb={thumbnails[id]} onClick={setActiveVideo} />
              ))}
            </div>
            <div className="flex flex-col gap-5">
              {topGrid.slice(16, 19).map((id) => (
                <Thumbnail key={id} vimeoId={id} thumb={thumbnails[id]} onClick={setActiveVideo} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {bottomGrid.map((id) => (
              <Thumbnail key={id} vimeoId={id} thumb={thumbnails[id]} onClick={setActiveVideo} />
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <VideoModal
        vimeoId={activeVideo}
        open={!!activeVideo}
        onOpenChange={(open) => !open && setActiveVideo(null)}
      />
    </div>
  );
};

interface ThumbnailProps {
  vimeoId: string;
  thumb?: string;
  onClick: (id: string) => void;
}

const Thumbnail = ({ vimeoId, thumb, onClick }: ThumbnailProps) => (
  <button
    onClick={() => onClick(vimeoId)}
    className="group relative overflow-hidden rounded-sm w-full cursor-pointer border-0 p-0 bg-transparent"
  >
    {thumb ? (
      <img
        src={thumb}
        alt="Video thumbnail"
        className="w-full h-auto block transition-transform duration-300 group-hover:scale-105"
        loading="lazy"
      />
    ) : (
      <Skeleton className="w-full aspect-video" />
    )}
    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
      <div className="w-16 h-16 rounded-full bg-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div
          className="w-0 h-0 border-t-8 border-b-8 border-t-transparent border-b-transparent ml-1"
          style={{ borderLeftWidth: 14, borderLeftColor: "#000" }}
        />
      </div>
    </div>
  </button>
);

export default VideoPortfolio;
